import React, { useState, useEffect } from "react";
import { Header } from "../../components/modules";
import { FiSettings, FiPlus, FiTrash2, FiSave, FiUpload } from "react-icons/fi";
import { Spinner } from "reactstrap";
import { TbPhotoPlus } from "react-icons/tb";
import { resizeImage } from "../../util";
import Context from "../../Context";
import { Switch, useToast } from "../../components/ui";

export const Popup = () => {
  const { configs, callApi, updateConfig } = Context();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);

  // local states for batch update
  const [isOpen, setIsOpen] = useState(false);
  const [interval, setInterval] = useState(24);
  const [images, setImages] = useState({});

  useEffect(() => {
    if (configs?.popup) {
      setIsOpen(configs.popup.isOpen ?? false);
      setInterval(configs.popup.interval ?? 24);
      setImages(configs.popup.images ?? {});
    }
  }, [configs]);

  const handleAddPopup = () => {
    const keys = Object.keys(images).map(Number);
    const nextIndex = keys.length > 0 ? Math.max(...keys) + 1 : 1;
    setImages((prev) => ({
      ...prev,
      [nextIndex]: { url: "", go: "" },
    }));
    toast({
      className: "bg-[rgb(59,176,101)]",
      title: "เพิ่มการ์ดป๊อปอัพใหม่แล้ว (กรุณาอัปโหลดรูปภาพ)",
    });
  };

  const handleDeletePopup = (keyToDelete) => {
    setImages((prev) => {
      const next = { ...prev };
      delete next[keyToDelete];
      return next;
    });
    toast({
      className: "bg-[rgb(59,176,101)]",
      title: "ลบป๊อปอัพออกจากคิวแล้ว (กดบันทึกเพื่อมีผลจริง)",
    });
  };

  const handleGoChange = (key, value) => {
    setImages((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        go: value,
      },
    }));
  };

  const handleUploadImage = async (key, file) => {
    const base64 = await resizeImage(file);
    if (!base64) {
      toast({
        className: "bg-[#c15b78]",
        title: "อัปโหลดได้เฉพาะไฟล์รูปภาพ .jpg หรือ .png เท่านั้น",
      });
      return;
    }

    setUploadingKey(key);
    const result = await callApi(
      "configs-editPopup",
      {
        image: base64,
        index: key,
      },
      { notShowLoading: true }
    );
    setUploadingKey(null);

    if (result && result.status !== false) {
      toast({
        className: "bg-[rgb(59,176,101)]",
        title: "อัปโหลดและประมวลผลรูปภาพสำเร็จ",
      });
      updateConfig();
    } else {
      toast({
        className: "bg-[#c15b78]",
        title: "อัปโหลดรูปภาพล้มเหลว กรุณาลองใหม่อีกครั้ง",
      });
    }
  };

  const handleSaveAll = async () => {
    setLoading(true);
    const { status } = await callApi(
      "configs-edit",
      {
        id: "general",
        key: "popup",
        value: {
          isOpen,
          interval: Number(interval),
          images,
        },
      },
      { notShowLoading: true }
    );
    setLoading(false);

    if (status) {
      toast({
        className: "bg-[rgb(59,176,101)]",
        title: "บันทึกการตั้งค่าป๊อปอัพทั้งหมดสำเร็จ",
      });
      updateConfig();
    } else {
      toast({
        className: "bg-[#c15b78]",
        title: "บันทึกข้อมูลล้มเหลว กรุณาลองใหม่อีกครั้ง",
      });
    }
  };

  return (
    <div className="h-fit min-w-[600px] w-full pb-12">
      <Header
        header="Settings"
        subHeader="ตั้งค่าป๊อปอัพแสดงหน้าแรก"
        icon={<FiSettings className="text-blue" />}
      />

      {/* General Settings Section */}
      <div className="relative rounded-xl mt-6 overflow-hidden shadow-lg border border-[#E5E7E9] bg-white p-5">
        <h3 className="text-lg font-bold text-navy mb-4 border-b pb-2 flex items-center gap-2">
          <span>⚙️</span> การตั้งค่าทั่วไป
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex justify-between items-center bg-gray-50 p-4 rounded-xl border border-gray-100">
            <div>
              <p className="font-bold text-navy mb-0.5">เปิดใช้งานระบบป๊อปอัพ</p>
              <p className="text-xs text-gray-500 mb-0">เปิดแสดงป๊อปอัพโฆษณาที่หน้าแรกสำหรับลูกค้า</p>
            </div>
            <Switch
              disabled={!configs || loading}
              checked={isOpen}
              onCheckedChange={(val) => setIsOpen(val)}
            />
          </div>

          <div className="flex justify-between items-center bg-gray-50 p-4 rounded-xl border border-gray-100">
            <div>
              <p className="font-bold text-navy mb-0.5">ระยะเวลาแสดงซ้ำ (ชั่วโมง)</p>
              <p className="text-xs text-gray-500 mb-0">ป๊อปอัพจะแสดงซ้ำอีกครั้งหลังจากปิดไปแล้วกี่ชั่วโมง</p>
            </div>
            <input
              type="number"
              min="0"
              className="border border-gray-300 w-24 text-center py-1.5 px-2 font-bold rounded-lg focus:outline-none focus:border-blue placeholder:text-sm disabled:opacity-65"
              disabled={!configs || loading}
              value={interval}
              onChange={(e) => setInterval(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Popups Grid Section */}
      <div className="mt-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-navy flex items-center gap-2 mb-0">
            <span>🖼️</span> รายการรูปภาพป๊อปอัพ
          </h3>
          <button
            type="button"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue text-white rounded-lg font-semibold hover:opacity-90 duration-150 shadow-sm"
            onClick={handleAddPopup}
            disabled={loading}
          >
            <FiPlus className="text-lg" /> เพิ่มป๊อปอัพใหม่
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Object.entries(images).map(([index, popup]) => {
            const isUploading = uploadingKey === index;
            return (
              <div
                key={index}
                className="relative flex flex-col bg-white rounded-xl border border-[#E5E7E9] overflow-hidden shadow-sm hover:shadow-md duration-200"
              >
                {/* Card Header with delete button */}
                <div className="flex justify-between items-center bg-gray-50 px-4 py-2.5 border-b border-gray-100">
                  <span className="font-bold text-navy text-sm">ป๊อปอัพลำดับที่ {index}</span>
                  <button
                    type="button"
                    className="p-1 text-gray-400 hover:text-danger rounded-md duration-150"
                    onClick={() => handleDeletePopup(index)}
                    title="ลบป๊อปอัพนี้"
                  >
                    <FiTrash2 className="text-md" />
                  </button>
                </div>

                {/* Card Body - Image Upload & Preview */}
                <div className="p-4 flex-grow flex flex-col justify-between">
                  <div className="relative group flex items-center justify-center border-2 border-dashed border-gray-200 rounded-lg aspect-[3/4] overflow-hidden bg-gray-50 mb-4">
                    {isUploading ? (
                      <div className="flex flex-col items-center gap-2">
                        <Spinner className="text-blue w-6 h-6" />
                        <span className="text-xs text-gray-500 font-semibold">กำลังอัปโหลด...</span>
                      </div>
                    ) : popup.url ? (
                      <>
                        <img
                          src={popup.url}
                          alt={`Popup ${index}`}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 duration-200 flex items-center justify-center">
                          <label
                            htmlFor={`upload-${index}`}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-white/90 rounded-md text-navy font-bold text-xs cursor-pointer hover:bg-white duration-150"
                          >
                            <FiUpload /> เปลี่ยนรูปภาพ
                          </label>
                        </div>
                      </>
                    ) : (
                      <label
                        htmlFor={`upload-${index}`}
                        className="flex flex-col items-center gap-2 cursor-pointer py-10 w-full h-full justify-center hover:bg-gray-100/50 duration-150"
                      >
                        <TbPhotoPlus className="w-10 h-10 text-gray-400" />
                        <span className="text-xs font-bold text-gray-500">อัปโหลดรูปภาพ</span>
                      </label>
                    )}

                    <input
                      id={`upload-${index}`}
                      type="file"
                      accept="image/jpeg, image/png"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleUploadImage(index, file);
                      }}
                    />
                  </div>

                  {/* Card Footer - URL Target */}
                  <div>
                    <label className="text-xs font-bold text-gray-500 block mb-1">
                      ลิงก์ปลายทาง (เมื่อคลิกป๊อปอัพ)
                    </label>
                    <input
                      type="text"
                      className="border border-gray-300 w-full py-1.5 px-2 text-sm font-semibold rounded-lg focus:outline-none focus:border-blue"
                      placeholder="https://example.com"
                      value={popup.go || ""}
                      onChange={(e) => handleGoChange(index, e.target.value)}
                    />
                  </div>
                </div>
              </div>
            );
          })}

          {/* Empty Add Card Placeholder */}
          <div
            onClick={handleAddPopup}
            className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl min-h-[300px] bg-gray-50/50 hover:bg-gray-50 hover:border-blue cursor-pointer duration-150 group"
          >
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-100 group-hover:scale-110 duration-200">
              <FiPlus className="text-xl text-gray-400 group-hover:text-blue" />
            </div>
            <span className="text-sm font-bold text-gray-500 mt-3 group-hover:text-blue">
              เพิ่มป๊อปอัพใหม่
            </span>
          </div>
        </div>
      </div>

      {/* Floating Save Actions Footer */}
      <div className="fixed bottom-6 right-6 z-40">
        <button
          type="button"
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue to-[#2065C4] text-white font-bold rounded-full shadow-lg hover:opacity-90 active:scale-95 duration-150"
          onClick={handleSaveAll}
          disabled={loading}
        >
          {loading ? (
            <Spinner className="w-4 h-4 text-white" />
          ) : (
            <FiSave className="text-lg" />
          )}
          บันทึกการเปลี่ยนแปลงทั้งหมด
        </button>
      </div>
    </div>
  );
};
